/**
 * DecorIcons.ts
 * Handcrafted 2.5D Isometric Cel-Shaded Vector SVG Iconography for:
 * - 6 Decor Categories (ALL, BED, FRIDGE, DESK, SOFA, PLANT)
 * - 15 Furniture Catalog items across 3 progression tiers
 *   (Pilot Batch: 10 Tier 1 & Tier 2 items upgraded to Smooth Stylized 2.5D Vector / Cel-Shaded art
 *    synchronized 100% in perspective and palette with PixelArtGenerator room sprites)
 * - Legacy flat-box SVG archive for Before vs. After studio comparison
 */

export const DECOR_CATEGORY_SVG_ICONS: Record<string, string> = {
  ALL: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_cat_all_g1" x1="4" y1="2" x2="20" y2="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#ea580c"/>
      </linearGradient>
    </defs>
    <path d="M12 2L14.8 8.8L21.5 11.5L14.8 14.2L12 21L9.2 14.2L2.5 11.5L9.2 8.8Z" fill="url(#dc_cat_all_g1)" stroke="#1e1b2e" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M12 4.5L13.6 9.5L18.5 11.5L13.6 13.5L12 18.5" stroke="#ffffff" stroke-width="1" stroke-linecap="round" opacity="0.65"/>
    <path d="M18.5 3L19.4 5.6L22 6.5L19.4 7.4L18.5 10L17.6 7.4L15 6.5L17.6 5.6Z" fill="#fde047" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>`,

  BED: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_cat_bed_g1" x1="2" y1="8" x2="22" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#818cf8"/>
        <stop offset="100%" stop-color="#4338ca"/>
      </linearGradient>
    </defs>
    <!-- 2.5D Headboard -->
    <path d="M3 11L11 7L13 8V14L5 18L3 17Z" fill="#b45309" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Isometric Mattress & Duvet -->
    <path d="M5 13L13 9L21 13L13 17Z" fill="url(#dc_cat_bed_g1)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M5 13L13 17V20L5 16Z" fill="#312e81" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M13 17L21 13V16L13 20Z" fill="#3730a3" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Plump Pillow -->
    <path d="M7.5 11.5L11.5 9.5L14 10.8L10 12.8Z" fill="#ffffff" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>`,

  FRIDGE: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_cat_fridge_g1" x1="6" y1="3" x2="19" y2="21" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#67e8f9"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
    </defs>
    <!-- Top Diamond -->
    <path d="M12 2.5L19 6L12 9.5L5 6Z" fill="#bae6fd" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Left Shaded Side -->
    <path d="M5 6L12 9.5V21.5L5 18Z" fill="#0369a1" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Front-Right Door -->
    <path d="M12 9.5L19 6V18L12 21.5Z" fill="url(#dc_cat_fridge_g1)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Freezer Seam & Chrome Handle -->
    <line x1="12" y1="14" x2="19" y2="10.5" stroke="#1e1b2e" stroke-width="1.3"/>
    <line x1="13.8" y1="10.5" x2="13.8" y2="12.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="13.8" y1="15.2" x2="13.8" y2="18.2" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,

  DESK: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- Isometric Tabletop -->
    <path d="M3 13L11 9L21 14L13 18Z" fill="#d97706" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Legs -->
    <path d="M5 14V20M13 18V22M19 15V20" stroke="#475569" stroke-width="1.8" stroke-linecap="round"/>
    <!-- Glowing Monitor -->
    <path d="M9 5.5L16 9V13.5L9 10Z" fill="#38bdf8" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="12.5" y1="11.8" x2="12.5" y2="13.8" stroke="#1e1b2e" stroke-width="1.6"/>
  </svg>`,

  SOFA: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_cat_sofa_g1" x1="3" y1="6" x2="21" y2="19" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f472b6"/>
        <stop offset="100%" stop-color="#db2777"/>
      </linearGradient>
    </defs>
    <!-- Backrest -->
    <path d="M5 8L14 4L19 6.5V12L10 16.5L5 14Z" fill="#be185d" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Plush Seat -->
    <path d="M4 13L13 8.5L20 12L11 16.5Z" fill="url(#dc_cat_sofa_g1)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M4 13L11 16.5V19.5L4 16Z" fill="#9d174d" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M11 16.5L20 12V15L11 19.5Z" fill="#be185d" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
  </svg>`,

  PLANT: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <!-- 2.5D Terracotta Pot -->
    <path d="M7.5 14.5L16.5 14.5L15 21C15 21.8 9 21.8 9 21Z" fill="#ea580c" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <ellipse cx="12" cy="14.5" rx="5" ry="1.8" fill="#fb923c" stroke="#1e1b2e" stroke-width="1.3"/>
    <!-- Lush Monstera / Cactus Leaves -->
    <path d="M12 14C11 9 6 7 5 9C4 11 8 13.5 12 14Z" fill="#22c55e" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M12 14C13 9 18 7 19 9C20 11 16 13.5 12 14Z" fill="#16a34a" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M12 14C10 8 10 3 12 2.5C14 3 14 8 12 14Z" fill="#4ade80" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
  </svg>`,
};

/**
 * Archive of the pre-overhaul flat placeholder icons for Before vs. After comparison in DecorPilotShowcase.
 */
export const LEGACY_FURNITURE_SVG_ICONS: Record<string, string> = {
  bed_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="22" width="32" height="14" rx="3" fill="#cbd5e1" stroke="#94a3b8" stroke-width="1.5"/>
    <rect x="10" y="24" width="10" height="10" rx="2" fill="#ffffff" stroke="#cbd5e1" stroke-width="1"/>
    <path d="M22 24 L 38 24 L 38 34 L 22 34 Z" fill="#93c5fd"/>
  </svg>`,
  bed_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="14" width="6" height="24" rx="2" fill="#78350f"/>
    <rect x="36" y="22" width="6" height="16" rx="2" fill="#78350f"/>
    <rect x="10" y="24" width="28" height="10" rx="2" fill="#38bdf8" stroke="#0284c7" stroke-width="1.2"/>
    <rect x="12" y="20" width="7" height="6" rx="2" fill="#ffffff"/>
    <rect x="12" y="26" width="7" height="6" rx="2" fill="#ffffff"/>
  </svg>`,
  fridge_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="16" width="24" height="24" rx="3" fill="#334155" stroke="#64748b" stroke-width="2"/>
    <rect x="15" y="19" width="18" height="18" rx="2" fill="#1e293b"/>
    <line x1="16" y1="24" x2="16" y2="28" stroke="#38bdf8" stroke-width="2" stroke-linecap="round"/>
    <circle cx="30" cy="22" r="1.5" fill="#38bdf8"/>
  </svg>`,
  fridge_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="12" y="8" width="24" height="34" rx="4" fill="#475569" stroke="#94a3b8" stroke-width="1.8"/>
    <rect x="14" y="10" width="20" height="10" rx="2" fill="#334155"/>
    <line x1="16" y1="16" x2="16" y2="18" stroke="#f8fafc" stroke-width="1.8" stroke-linecap="round"/>
    <rect x="14" y="22" width="20" height="18" rx="2" fill="#1e293b"/>
    <line x1="16" y1="26" x2="16" y2="32" stroke="#f8fafc" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`,
  desk_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="20" width="32" height="4" rx="1" fill="#78350f" stroke="#451a03" stroke-width="1"/>
    <line x1="12" y1="24" x2="10" y2="38" stroke="#64748b" stroke-width="2"/>
    <line x1="36" y1="24" x2="38" y2="38" stroke="#64748b" stroke-width="2"/>
    <path d="M14 20 L 16 12 L 20 14" stroke="#fbbf24" stroke-width="1.5" stroke-linecap="round"/>
    <polygon points="18,12 24,15 21,18" fill="#fbbf24"/>
  </svg>`,
  desk_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="22" width="32" height="3" rx="1" fill="#334155"/>
    <line x1="14" y1="25" x2="14" y2="38" stroke="#0ea5e9" stroke-width="2.5"/>
    <line x1="34" y1="25" x2="34" y2="38" stroke="#0ea5e9" stroke-width="2.5"/>
    <rect x="14" y="10" width="20" height="10" rx="1.5" fill="#0f172a" stroke="#38bdf8" stroke-width="1.5"/>
    <line x1="24" y1="20" x2="24" y2="22" stroke="#94a3b8" stroke-width="2"/>
  </svg>`,
  sofa_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="24" cy="28" rx="16" ry="12" fill="#f59e0b" stroke="#d97706" stroke-width="1.8"/>
    <ellipse cx="24" cy="24" rx="9" ry="6" fill="#fbbf24"/>
    <circle cx="24" cy="18" r="1.5" fill="#b45309"/>
  </svg>`,
  sofa_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 16 C 10 14, 38 14, 38 16 L 38 30 L 10 30 Z" fill="#818cf8"/>
    <rect x="8" y="24" width="32" height="10" rx="3" fill="#6366f1" stroke="#4f46e5" stroke-width="1.5"/>
    <line x1="24" y1="24" x2="24" y2="34" stroke="#4338ca" stroke-width="1.5"/>
    <line x1="12" y1="34" x2="10" y2="40" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
    <line x1="36" y1="34" x2="38" y2="40" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
  </svg>`,
  plant_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon points="16,28 32,28 30,42 18,42" fill="#c2410c" stroke="#9a3412" stroke-width="1.5"/>
    <rect x="21" y="14" width="6" height="15" rx="3" fill="#15803d" stroke="#166534" stroke-width="1"/>
    <path d="M21 20 L 16 20 L 16 16" stroke="#166534" stroke-width="2" stroke-linecap="round" fill="none"/>
    <circle cx="24" cy="13" r="2" fill="#f43f5e"/>
  </svg>`,
  plant_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="16" y="30" width="16" height="12" rx="3" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.5"/>
    <path d="M24 30 Q 18 16 10 18 Q 16 26 24 30 Z" fill="#22c55e" stroke="#15803d" stroke-width="1"/>
    <path d="M24 30 Q 30 16 38 18 Q 32 26 24 30 Z" fill="#16a34a" stroke="#15803d" stroke-width="1"/>
    <path d="M24 30 Q 24 10 24 8 Q 28 18 24 30 Z" fill="#4ade80" stroke="#15803d" stroke-width="1"/>
  </svg>`,
};

export const FURNITURE_SVG_ICONS: Record<string, string> = {
  // =========================================================================
  // BED TIERS (1-3)
  // =========================================================================

  // 1. bed_t1: Đệm Futon Đơn Giản / Futon Mat (Tier 1, 2x1)
  // True 2.5D isometric slatted blonde birch wooden pallet base, thick quilted indigo futon mattress
  // with 3D side/front bevels, folded cream throw blanket with soft drape, plump white pillow with seam stitching.
  bed_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_bed_t1_pallet" x1="4" y1="18" x2="44" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde68a"/>
        <stop offset="55%" stop-color="#e3b16d"/>
        <stop offset="100%" stop-color="#b88148"/>
      </linearGradient>
      <linearGradient id="dc_bed_t1_mattress" x1="8" y1="14" x2="40" y2="32" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6366f1"/>
        <stop offset="55%" stop-color="#4338ca"/>
        <stop offset="100%" stop-color="#312e81"/>
      </linearGradient>
      <linearGradient id="dc_bed_t1_blanket" x1="20" y1="18" x2="42" y2="34" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef3c7"/>
        <stop offset="65%" stop-color="#fcd34d"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
      <linearGradient id="dc_bed_t1_pillow" x1="10" y1="12" x2="24" y2="21" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="100%" stop-color="#cbd5e1"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Isometric Shadow -->
    <path d="M18 14L46 28L29 39L2 25Z" fill="rgba(15, 23, 42, 0.42)"/>
    <!-- Birch Wooden Pallet Base (Top + Left Bevel + Right Bevel) -->
    <path d="M18 14.5L44.5 27.8L29 37L2.5 23.8Z" fill="url(#dc_bed_t1_pallet)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M2.5 23.8L29 37V40.2L2.5 27Z" fill="#b88148" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M29 37L44.5 27.8V31L29 40.2Z" fill="#8a583e" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Pallet Slat Grooves & Espresso Support Blocks -->
    <line x1="7.5" y1="21" x2="33.5" y2="34" stroke="#8a583e" stroke-width="0.9"/>
    <line x1="12.5" y1="18" x2="38.5" y2="31" stroke="#8a583e" stroke-width="0.9"/>
    <line x1="9" y1="28.5" x2="9" y2="30.2" stroke="#24171e" stroke-width="1.8"/>
    <line x1="18" y1="33" x2="18" y2="34.8" stroke="#24171e" stroke-width="1.8"/>
    <line x1="26" y1="37" x2="26" y2="38.8" stroke="#24171e" stroke-width="1.8"/>
    <!-- Thick Quilted Indigo Futon Mattress (Top + 3D Side Bevels) -->
    <path d="M18 11.8L42.5 24L28.5 32.2L4 20Z" fill="url(#dc_bed_t1_mattress)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M4 20L28.5 32.2V36L4 23.8Z" fill="#312e81" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M28.5 32.2L42.5 24V27.8L28.5 36Z" fill="#1e1b4b" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Folded Cream Throw Blanket Draped over Foot of Futon -->
    <path d="M25.5 15.5L42.5 24L28.5 32.2L14 25Z" fill="url(#dc_bed_t1_blanket)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M14 25L28.5 32.2V35.8L14 28.6Z" fill="#f59e0b" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M28.5 32.2L42.5 24V27.5L28.5 35.8Z" fill="#b45309" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Tufted Stitch Dimples & Blanket Stripe -->
    <path d="M18.5 27.2L32.5 19" stroke="#d97706" stroke-width="1.1" stroke-dasharray="2 1.8"/>
    <circle cx="21" cy="21.5" r="0.9" fill="#a5b4fc"/>
    <circle cx="15" cy="21.2" r="0.9" fill="#a5b4fc"/>
    <!-- Plump White Linen Pillow with Seam Stitching -->
    <path d="M17.5 13L25.5 17C26.2 17.5 26.2 18.5 25.2 19L19.5 22.4C18.5 22.9 17.2 22.8 16.4 22.3L8.8 18.4C8 17.9 8 16.9 8.9 16.4L14.8 13C15.6 12.5 16.8 12.6 17.5 13Z" fill="url(#dc_bed_t1_pillow)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M12.5 16.8Q17 18.5 21.5 17.2" stroke="#94a3b8" stroke-width="1" stroke-linecap="round"/>
    <!-- Specular Highlight Rim -->
    <path d="M15.5 13.8L23 17.5" stroke="#ffffff" stroke-width="1.1" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  // 2. bed_t2: Giường Gỗ Sồi Ấm Cúng / Wooden Bed Frame (Tier 2, 2x1)
  // True 2.5D isometric warm oak bed frame with walnut paneled headboard, plush mattress,
  // coral-sunset duvet folded back over a cream sheet, two plump pillows, and attached nightstand with glowing brass lamp.
  bed_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_bed_t2_duvet" x1="14" y1="18" x2="42" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb7185"/>
        <stop offset="55%" stop-color="#e11d48"/>
        <stop offset="100%" stop-color="#9f1239"/>
      </linearGradient>
      <linearGradient id="dc_bed_t2_oak" x1="2" y1="14" x2="44" y2="40" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f59e0b"/>
        <stop offset="55%" stop-color="#b45309"/>
        <stop offset="100%" stop-color="#78350f"/>
      </linearGradient>
      <radialGradient id="dc_bed_t2_lamp" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stop-color="#fef08a" stop-opacity="0.95"/>
        <stop offset="60%" stop-color="#f59e0b" stop-opacity="0.45"/>
        <stop offset="100%" stop-color="#f59e0b" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <!-- Ground Ambient Occlusion Shadow -->
    <path d="M16 14L46 29L28 42L1 27Z" fill="rgba(15, 23, 42, 0.45)"/>
    <!-- Attached Bedside Nightstand & Glowing Warm Brass Lamp (Back-Right beside Headboard) -->
    <circle cx="24.5" cy="7.2" r="6.2" fill="url(#dc_bed_t2_lamp)"/>
    <path d="M21 10.5L28 14L24.5 16L17.5 12.5Z" fill="#d97706" stroke="#1e1b2e" stroke-width="1.15" stroke-linejoin="round"/>
    <path d="M24.5 16L28 14V19.5L24.5 21.5Z" fill="#78350f" stroke="#1e1b2e" stroke-width="1.15" stroke-linejoin="round"/>
    <line x1="24.5" y1="8.8" x2="24.5" y2="12.5" stroke="#fbbf24" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M21.8 8.8L23 5H26L27.2 8.8Z" fill="#fef08a" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- 3D Isometric Extruded Walnut Paneled Headboard along Back-Left Head Edge -->
    <path d="M2.2 13.4L16.5 4.8L18.5 5.8L4.2 14.4Z" fill="#b45309" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M2.2 13.4L4.2 14.4V23.2L2.2 22.2Z" fill="#451a03" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M4.2 14.4L18.5 5.8V14.6L4.2 23.2Z" fill="#633833" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Recessed Twin Walnut Headboard Panels -->
    <path d="M5.6 15.2L10.8 12.1V17.4L5.6 20.5Z" fill="#8a583e" stroke="#24171e" stroke-width="0.95" stroke-linejoin="round"/>
    <path d="M12 11.4L17.2 8.3V13.6L12 16.7Z" fill="#8a583e" stroke="#24171e" stroke-width="0.95" stroke-linejoin="round"/>
    <!-- Solid Oak Bed Frame & Footboard -->
    <path d="M3.5 22.5L28 34.8L43.5 25.8V30.5L28 39.5L3.5 27.2Z" fill="url(#dc_bed_t2_oak)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M28 34.8L43.5 25.8V30.5L28 39.5Z" fill="#78350f" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Oak Corner Legs -->
    <path d="M4 27.2V30.2M28 39.2V42M43 30.2V33" stroke="#451a03" stroke-width="2.2" stroke-linecap="round"/>
    <!-- Plush Fitted Cream Sheet & Mattress -->
    <path d="M18.2 13.5L42.5 25.6L28 34.2L4.2 22.2Z" fill="#fffbeb" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Two Plump Isometric Pillows Side-by-Side Along Headboard -->
    <path d="M11.2 16.6L17.2 19.6L12.8 22.4L6.8 19.4Z" fill="#ffffff" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M17.2 13L23.2 16L18.8 18.8L12.8 15.8Z" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- Coral-Sunset Quilted Duvet with Folded Cream Turn-Down Collar -->
    <path d="M27.8 18.3L42.5 25.6L28 34.2L13.2 26.8Z" fill="url(#dc_bed_t2_duvet)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M13.2 26.8L28 34.2V37.6L13.2 30.2Z" fill="#be123c" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M28 34.2L42.5 25.6V29L28 37.6Z" fill="#881337" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Cream Fold-Back Duvet Collar Spanning Parallel to Headboard -->
    <path d="M25.2 17L28.5 18.6L14 27.2L10.8 25.6Z" fill="#fef3c7" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- Specular Highlight -->
    <path d="M16 26.5L27.5 32.2" stroke="#fda4af" stroke-width="1.1" stroke-linecap="round" opacity="0.8"/>
  </svg>`,

  // 3. bed_t3: Giường Hoàng Gia King Size / Luxury California King Canopy & Velvet Smart Bed (Tier 3, 2x1)
  // True 2.5D isometric California King smart canopy bed with brushed brass 4-poster canopy frame,
  // tufted royal plum velvet headboard with warm ambient LED cove lighting, plush quilted velvet duvet,
  // gold-piped satin pillows, and integrated smart bedside control console.
  bed_t3: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_bed_t3_velvet" x1="6" y1="16" x2="42" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#a855f7"/>
        <stop offset="50%" stop-color="#6b21a8"/>
        <stop offset="100%" stop-color="#3b0764"/>
      </linearGradient>
      <linearGradient id="dc_bed_t3_headboard" x1="4" y1="5" x2="32" y2="26" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#7e22ce"/>
        <stop offset="100%" stop-color="#4c1d95"/>
      </linearGradient>
      <linearGradient id="dc_bed_t3_gold" x1="2" y1="2" x2="46" y2="34" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#ca8a04"/>
      </linearGradient>
    </defs>
    <!-- Smart Underglow Halo & Ground Ambient Occlusion -->
    <path d="M16 14L46 28.5L28 41L1.5 26.5Z" fill="rgba(6, 182, 212, 0.34)"/>
    <!-- 2.5D Tufted Royal Velvet Headboard with Brushed Brass Frame -->
    <path d="M3.5 12.5L18.5 4.5L32.5 11.5V22.5L17.5 30.5L3.5 23.5Z" fill="url(#dc_bed_t3_headboard)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M3.5 12.5L18.5 4.5L32.5 11.5" stroke="url(#dc_bed_t3_gold)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    <!-- Diamond Tufting & Gold Buttons on Headboard -->
    <circle cx="12" cy="13.5" r="1.15" fill="#facc15" stroke="#1e1b2e" stroke-width="0.6"/>
    <circle cx="18" cy="11.5" r="1.15" fill="#facc15" stroke="#1e1b2e" stroke-width="0.6"/>
    <circle cx="24" cy="14.5" r="1.15" fill="#facc15" stroke="#1e1b2e" stroke-width="0.6"/>
    <!-- Floating Espresso & Gold Platform Base -->
    <path d="M3.5 22.8L28 35.2L43.8 26V30.4L28 39.6L3.5 27.2Z" fill="#1e1b4b" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M4 26.8L28 39L43.2 29.8" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Cashmere Cream Fitted Sheet & Royal Velvet Quilted Duvet -->
    <path d="M18 13.6L43.2 26.2L28 35.2L3.8 22.8Z" fill="#fffbeb" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M26.5 17.8L43.2 26.2L28 35.2L11.5 26.8Z" fill="url(#dc_bed_t3_velvet)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M11.5 26.8L28 35.2V38.4L11.5 30Z" fill="#4c1d95" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M28 35.2L43.2 26.2V29.4L28 38.4Z" fill="#2e1065" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Gold-Piped Satin Pillows -->
    <path d="M12.5 16L19.5 19.5L15 22.5L8 19Z" fill="#ffffff" stroke="#eab308" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M19.5 12.2L26.5 15.7L22 18.7L15 15.2Z" fill="#fefce8" stroke="#eab308" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- 4-Poster Brushed Brass Canopy Frame Rails -->
    <path d="M4 12.5V4.5L18.5 1.5L43 13.5V26" stroke="url(#dc_bed_t3_gold)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M28 35.2V21.5L43 13.5" stroke="url(#dc_bed_t3_gold)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  // =========================================================================
  // FRIDGE TIERS (1-3)
  // =========================================================================

  // 3. fridge_t1: Tủ Lạnh Mini Sinh Viên / Mini Cooler (Tier 1, 1x1)
  // True 2.5D isometric retro mint/slate mini-fridge box (showing top face, front-right door face,
  // and left side face), curved chrome handle, colorful souvenir magnet stickers, glowing cyan LED, specular sheen.
  fridge_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_fridge_t1_top" x1="10" y1="8" x2="38" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ccfbf1"/>
        <stop offset="100%" stop-color="#5eead4"/>
      </linearGradient>
      <linearGradient id="dc_fridge_t1_door" x1="24" y1="16" x2="39" y2="41" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#2dd4bf"/>
        <stop offset="55%" stop-color="#0d9488"/>
        <stop offset="100%" stop-color="#0f766e"/>
      </linearGradient>
      <linearGradient id="dc_fridge_t1_side" x1="9" y1="14" x2="24" y2="41" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#115e59"/>
        <stop offset="100%" stop-color="#134e4a"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Isometric Shadow -->
    <path d="M24 31L42 38.5L24 45.5L6 38.5Z" fill="rgba(15, 23, 42, 0.44)"/>
    <!-- Rubber Leveling Feet -->
    <path d="M11 36.5V39M24 42.5V44.8M37 36.5V39" stroke="#0f172a" stroke-width="2.4" stroke-linecap="round"/>
    <!-- 2.5D Top Enamel Face -->
    <path d="M24 8.5L39 15.5L24 22.5L9 15.5Z" fill="url(#dc_fridge_t1_top)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- 2.5D Shaded Left Side Cabinet Face -->
    <path d="M9 15.5L24 22.5V43L9 36Z" fill="url(#dc_fridge_t1_side)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- 2.5D Front-Right Glossy Enamel Door Face -->
    <path d="M24 22.5L39 15.5V36L24 43Z" fill="url(#dc_fridge_t1_door)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- Inner Door Gasket Seam Line -->
    <line x1="25.8" y1="21.8" x2="25.8" y2="42" stroke="#042f2e" stroke-width="1.1"/>
    <!-- Diagonal Enamel Specular Sheen on Door -->
    <path d="M28 22L36 18.2V23.5L28 27.3Z" fill="rgba(255, 255, 255, 0.26)"/>
    <!-- Curved Retro Chrome Latch Handle -->
    <path d="M27.2 26.5C26.2 26.5 26.2 31.5 27.2 31.5" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M27.2 26.5L28.5 25.8" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round"/>
    <!-- Glowing Cyan Power LED Indicator -->
    <circle cx="35.5" cy="19.8" r="2.3" fill="rgba(34, 211, 238, 0.38)"/>
    <circle cx="35.5" cy="19.8" r="1.3" fill="#22d3ee" stroke="#083344" stroke-width="0.7"/>
    <!-- Colorful Souvenir Magnet Stickers (NY Red Apple & Sunny Postcard) -->
    <circle cx="31.5" cy="28.5" r="2.1" fill="#f43f5e" stroke="#1e1b2e" stroke-width="0.9"/>
    <path d="M33 32.5L36.5 31L36.5 34.5L33 36Z" fill="#facc15" stroke="#1e1b2e" stroke-width="0.9" stroke-linejoin="round"/>
    <!-- Stainless Espresso Moka Pot Resting on Top Face -->
    <path d="M21 12L26 12L25.2 16.5L21.8 16.5Z" fill="#e2e8f0" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M20.5 10.2L26.5 10.2L26 12L21 12Z" fill="#94a3b8" stroke="#1e1b2e" stroke-width="1.1" stroke-linejoin="round"/>
    <circle cx="23.5" cy="9.2" r="1" fill="#1e1b2e"/>
    <path d="M26 11.2L28 12.2L27.2 14.5" stroke="#1e1b2e" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  // 4. fridge_t2: Tủ Lạnh Gia Đình 2 Ngăn / Standard Apartment Fridge (Tier 2, 1x1)
  // True 2.5D isometric tall brushed-stainless double-door apartment refrigerator (top freezer + bottom fridge),
  // 3D vertical metallic handles, chilled water dispenser recess with status LEDs, and crisp side/top bevels.
  fridge_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_fridge_t2_top" x1="9" y1="3" x2="39" y2="14" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f8fafc"/>
        <stop offset="100%" stop-color="#94a3b8"/>
      </linearGradient>
      <linearGradient id="dc_fridge_t2_side" x1="9" y1="9" x2="24" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#475569"/>
        <stop offset="100%" stop-color="#1e293b"/>
      </linearGradient>
      <linearGradient id="dc_fridge_t2_door" x1="24" y1="10" x2="40" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#e2e8f0"/>
        <stop offset="50%" stop-color="#94a3b8"/>
        <stop offset="100%" stop-color="#475569"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Isometric Shadow -->
    <path d="M24 33L42 40L24 46.5L6 40Z" fill="rgba(15, 23, 42, 0.48)"/>
    <!-- 2.5D Top Brushed Steel Cap -->
    <path d="M24 3.2L39.5 10.2L24 17.2L8.5 10.2Z" fill="url(#dc_fridge_t2_top)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- 2.5D Left Shaded Brushed Steel Cabinet -->
    <path d="M8.5 10.2L24 17.2V44.5L8.5 37.5Z" fill="url(#dc_fridge_t2_side)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- 2.5D Front-Right Doors (Top Freezer + Bottom Main Fridge) -->
    <path d="M24 17.2L39.5 10.2V37.5L24 44.5Z" fill="url(#dc_fridge_t2_door)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Deep Horizontal Isometric Freezer/Fridge Divider Channel -->
    <path d="M24 27L39.5 20" stroke="#0f172a" stroke-width="2.2"/>
    <path d="M24 27.8L39.5 20.8" stroke="#f8fafc" stroke-width="0.8" opacity="0.65"/>
    <!-- Diagonal Brushed Stainless Specular Sheen -->
    <path d="M29 15.5L37 12V16.5L29 20Z" fill="rgba(255, 255, 255, 0.32)"/>
    <path d="M29 29L38 25V30L29 34Z" fill="rgba(255, 255, 255, 0.22)"/>
    <!-- 3D Vertical Tubular Metallic Handles (Upper & Lower) -->
    <line x1="26.8" y1="18.2" x2="26.8" y2="24.2" stroke="#f8fafc" stroke-width="2" stroke-linecap="round"/>
    <line x1="26.8" y1="29.2" x2="26.8" y2="39.5" stroke="#f8fafc" stroke-width="2" stroke-linecap="round"/>
    <!-- Chilled Water & Ice Dispenser Recess on Lower Door with Status LEDs -->
    <path d="M31 26.5L36.8 23.8V31.2L31 33.8Z" fill="#0f172a" stroke="#334155" stroke-width="1" stroke-linejoin="round"/>
    <circle cx="33" cy="26.5" r="0.9" fill="#38bdf8"/>
    <circle cx="35" cy="25.6" r="0.9" fill="#4ade80"/>
    <line x1="33.8" y1="28.2" x2="33.8" y2="31.5" stroke="#64748b" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Souvenir Magnets on Upper Freezer Door -->
    <circle cx="32" cy="18.2" r="1.8" fill="#f43f5e" stroke="#1e1b2e" stroke-width="0.8"/>
    <circle cx="35.8" cy="17.2" r="1.9" fill="#facc15" stroke="#1e1b2e" stroke-width="0.8"/>
  </svg>`,

  // 5. fridge_t3: Tủ Lạnh Thông Minh Cảm Ứng / Smart French-Door Glass Hub Refrigerator (Tier 3, 1x1)
  // True 2.5D isometric obsidian-steel French-Door refrigerator with glowing cyan Family Hub touchscreen,
  // amber-illuminated dual wine/chiller glass window, ice/water dispenser, and bottom freezer drawer.
  fridge_t3: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_fridge_t3_body" x1="8" y1="4" x2="40" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#475569"/>
        <stop offset="50%" stop-color="#1e293b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="dc_fridge_t3_screen" x1="33" y1="13" x2="39" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
      <linearGradient id="dc_fridge_t3_wine" x1="25" y1="26" x2="32" y2="35" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fbbf24"/>
        <stop offset="100%" stop-color="#b45309"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Shadow & Cyan Smart Underglow -->
    <path d="M24 33L43 40.5L24 46.8L5 40.5Z" fill="rgba(6, 182, 212, 0.34)"/>
    <!-- 2.5D Top Matte Slate Cap -->
    <path d="M24 2.8L40.5 10L24 17.2L7.5 10Z" fill="#64748b" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- 2.5D Left Obsidian Cabinet Wall -->
    <path d="M7.5 10L24 17.2V44.8L7.5 37.5Z" fill="#0f172a" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- 2.5D Front-Right French Doors & Bottom Freezer Drawer -->
    <path d="M24 17.2L40.5 10V37.5L24 44.8Z" fill="url(#dc_fridge_t3_body)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- French Door Center Split & Horizontal Drawer Dividers -->
    <line x1="32.2" y1="13.6" x2="32.2" y2="30.5" stroke="#090d16" stroke-width="1.8"/>
    <path d="M24 34L40.5 26.8" stroke="#38bdf8" stroke-width="1.5"/>
    <!-- Illuminated Amber Wine & Craft Beverage Zone (Bottom-Left Door Window) -->
    <path d="M25.5 25.5L30.8 23.2V29.5L25.5 31.8Z" fill="url(#dc_fridge_t3_wine)" stroke="#1e1b2e" stroke-width="1" stroke-linejoin="round"/>
    <!-- Chilled Ice & Water Dispenser Recess (Upper-Left Door) -->
    <path d="M25.5 18.2L30.8 15.8V21.8L25.5 24.2Z" fill="#090d16" stroke="#334155" stroke-width="1" stroke-linejoin="round"/>
    <circle cx="28.2" cy="18.2" r="1" fill="#38bdf8"/>
    <!-- Glowing Smart Touchscreen Hub on Right French Door -->
    <path d="M33.6 15.2L39.2 12.8V23.8L33.6 26.2Z" fill="url(#dc_fridge_t3_screen)" stroke="#1e1b2e" stroke-width="1.1" stroke-linejoin="round"/>
    <line x1="34.8" y1="16.8" x2="38" y2="15.4" stroke="#ffffff" stroke-width="1.1" stroke-linecap="round"/>
    <line x1="34.8" y1="19.2" x2="37.4" y2="18.1" stroke="#fef08a" stroke-width="1.1" stroke-linecap="round"/>
    <!-- Brushed Brass & Chrome Drawer Handle -->
    <path d="M26.5 36.2L38.2 31" stroke="#facc15" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`,

  // =========================================================================
  // DESK TIERS (1-3)
  // =========================================================================

  // 5. desk_t1: Bàn Gấp Laptop Cũ / Old Folding Table (Tier 1, 2x1)
  // True 2.5D isometric warm pine tabletop on X-frame matte steel legs, featuring an open glowing
  // laptop with code lines on screen, a ceramic coffee mug with rising steam, spiral notebook, wireless mouse.
  desk_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_desk_t1_top" x1="4" y1="14" x2="44" y2="30" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde68a"/>
        <stop offset="55%" stop-color="#f59e0b"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
      <linearGradient id="dc_desk_t1_screen" x1="16" y1="8" x2="29" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#0369a1"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Shadow -->
    <path d="M17 24L45 36.5L30 44.5L3 32Z" fill="rgba(15, 23, 42, 0.36)"/>
    <!-- Matte Charcoal X-Frame Folding Steel Legs -->
    <path d="M7 22L14 36M14 24L6 35" stroke="#334155" stroke-width="2.1" stroke-linecap="round"/>
    <path d="M31 31L40 42M41 28L30 43" stroke="#1e293b" stroke-width="2.2" stroke-linecap="round"/>
    <line x1="10" y1="30" x2="35" y2="39" stroke="#475569" stroke-width="1.3"/>
    <!-- 2.5D Isometric Warm Pine Tabletop (Top + Left Bevel + Front-Right Bevel) -->
    <path d="M17 13.5L44.5 26.2L29.5 34.8L2.5 22Z" fill="url(#dc_desk_t1_top)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M2.5 22L29.5 34.8V37.6L2.5 24.8Z" fill="#b45309" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M29.5 34.8L44.5 26.2V29L29.5 37.6Z" fill="#78350f" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Spiral Notebook & Wireless Mouse -->
    <path d="M9.5 20L15 22.6L12 24.5L6.5 21.8Z" fill="#fef08a" stroke="#1e1b2e" stroke-width="1" stroke-linejoin="round"/>
    <ellipse cx="30.5" cy="27.5" rx="2" ry="1.2" transform="rotate(24 30.5 27.5)" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1"/>
    <!-- Open 2.5D Laptop (Keyboard Deck + Glowing Code Screen) -->
    <path d="M16 20.5L26.5 25.5L22 28.2L11.5 23.2Z" fill="#475569" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M17.5 10L28 15L26.5 25.5L16 20.5Z" fill="#0f172a" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M18.6 11.8L26.8 15.8L25.6 23.8L17.4 19.8Z" fill="url(#dc_desk_t1_screen)"/>
    <!-- Glowing Code Syntax Lines on Screen -->
    <line x1="19.5" y1="14.2" x2="23.5" y2="16.2" stroke="#4ade80" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="19.2" y1="16.4" x2="25" y2="19.2" stroke="#facc15" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="18.9" y1="18.6" x2="22.8" y2="20.5" stroke="#f8fafc" stroke-width="1.1" stroke-linecap="round"/>
    <!-- Ceramic Coffee Mug with Rising Steam -->
    <path d="M33.5 21.5H38V26.2C38 27.2 33.5 27.2 33.5 26.2Z" fill="#f97316" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <ellipse cx="35.75" cy="21.5" rx="2.25" ry="1" fill="#451a03" stroke="#1e1b2e" stroke-width="1"/>
    <path d="M38 22.8C39.5 22.8 39.5 25.2 38 25.2" stroke="#f97316" stroke-width="1.3" stroke-linecap="round"/>
    <!-- Wisping Steam Curves -->
    <path d="M34.8 19.2Q34 17.2 35.5 15.5" stroke="#f8fafc" stroke-width="1.1" stroke-linecap="round" opacity="0.85"/>
    <path d="M36.8 19.8Q37.8 17.8 36.5 16.2" stroke="#f8fafc" stroke-width="1.1" stroke-linecap="round" opacity="0.7"/>
  </svg>`,

  // 6. desk_t2: Bàn Công Thái Học 2 Màn Hình / Ergonomic Standing Tech Desk (Tier 2, 2x1)
  // True 2.5D isometric walnut ergonomic standing desk with motorized T-legs, dual curved widescreen
  // monitors on an articulating arm displaying glowing charts/code, mechanical keyboard, desk mat, studio lamp.
  desk_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_desk_t2_walnut" x1="3" y1="16" x2="45" y2="34" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#b45309"/>
        <stop offset="55%" stop-color="#78350f"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="dc_desk_t2_mon1" x1="8" y1="6" x2="24" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#0284c7"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="dc_desk_t2_mon2" x1="23" y1="11" x2="39" y2="26" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#312e81"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Shadow -->
    <path d="M16 25L45 38L29 46L2 33Z" fill="rgba(15, 23, 42, 0.44)"/>
    <!-- Motorized Standing Desk T-Legs & Stabilizer Feet -->
    <path d="M9 24V37M6 38.5L13 35" stroke="#1e293b" stroke-width="2.6" stroke-linecap="round"/>
    <path d="M34 34V43.5M30 45L38 41.5" stroke="#0f172a" stroke-width="2.6" stroke-linecap="round"/>
    <path d="M9 28V33M34 36V40" stroke="#38bdf8" stroke-width="1.1" stroke-linecap="round"/>
    <!-- 2.5D Ergonomic Walnut Tabletop -->
    <path d="M17 15.5L45.5 28.8L29.5 37.8L2 24.5Z" fill="url(#dc_desk_t2_walnut)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M2 24.5L29.5 37.8V40.4L2 27.1Z" fill="#451a03" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M29.5 37.8L45.5 28.8V31.4L29.5 40.4Z" fill="#24171e" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Extended Dark Felt Desk Mat with Cyan Trim -->
    <path d="M15.5 18.8L39.5 30L28.5 36.2L5.5 25Z" fill="#1e293b" stroke="#38bdf8" stroke-width="0.9" stroke-linejoin="round"/>
    <!-- Mechanical Keyboard with RGB & Cushioned Wrist Rest + Wireless Mouse -->
    <path d="M15 23L25.5 28L22.8 30L12.2 25Z" fill="#334155" stroke="#1e1b2e" stroke-width="0.9" stroke-linejoin="round"/>
    <path d="M12.2 25L22.8 30L21.4 31.2L10.8 26.2Z" fill="#0f172a" stroke="#1e1b2e" stroke-width="0.85" stroke-linejoin="round"/>
    <line x1="15.5" y1="24.5" x2="23" y2="28.2" stroke="#38bdf8" stroke-width="1.1" stroke-dasharray="1.5 1"/>
    <ellipse cx="29.5" cy="30.8" rx="1.8" ry="1.1" transform="rotate(25 29.5 30.8)" fill="#94a3b8" stroke="#1e1b2e" stroke-width="0.9"/>
    <!-- Articulating Dual-Monitor Mount Post & Studio Lamp Boom Arm -->
    <path d="M25 22.5V8.8" stroke="#475569" stroke-width="2.4" stroke-linecap="round"/>
    <!-- Left Widescreen Monitor (Code IDE) -->
    <path d="M10 6.5L24.5 12.2V22L10 16.2Z" fill="url(#dc_desk_t2_mon1)" stroke="#38bdf8" stroke-width="1.3" stroke-linejoin="round"/>
    <line x1="12.5" y1="10" x2="19.5" y2="12.8" stroke="#4ade80" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12.5" y1="12.6" x2="21.5" y2="16.2" stroke="#38bdf8" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="12.5" y1="15.2" x2="17.5" y2="17.2" stroke="#facc15" stroke-width="1.3" stroke-linecap="round"/>
    <!-- Right Curved Monitor (Analytics & Charts) -->
    <path d="M24.5 12.2L40.5 19.5V29.2L24.5 22Z" fill="url(#dc_desk_t2_mon2)" stroke="#38bdf8" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M27 21L30.5 19.5L34 22.5L38 19.8" stroke="#34d399" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="27.5" y1="16" x2="34.5" y2="19.2" stroke="#f472b6" stroke-width="1.3" stroke-linecap="round"/>
    <!-- Studio Task Lamp Arm & Glowing Light Bar Above Monitors -->
    <path d="M18 5.2L34 12.2L36 17L16 9Z" fill="rgba(254, 240, 138, 0.14)"/>
    <path d="M25 8.8L26 7.5" stroke="#94a3b8" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M18 5.2L34 12.2" stroke="#1e1b2e" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M18 5.2L34 12.2" stroke="#fef08a" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`,

  // 7. desk_t3: Dàn PC Cyber 3 Màn Hình RGB / Executive Quant & Studio Command Desk (Tier 3, 2x1)
  // True 2.5D isometric executive command desk with warm oak-brass edge trim, triple curved ultrawide
  // display array, studio acoustic monitors, RGB liquid-cooled glass PC tower, and custom split keyboard.
  desk_t3: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_desk_t3_top" x1="2" y1="16" x2="46" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#334155"/>
        <stop offset="55%" stop-color="#1e293b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="dc_desk_t3_scr_center" x1="16" y1="8" x2="32" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#06b6d4"/>
        <stop offset="100%" stop-color="#1e1b4b"/>
      </linearGradient>
      <linearGradient id="dc_desk_t3_scr_side" x1="5" y1="8" x2="42" y2="32" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#a855f7"/>
        <stop offset="100%" stop-color="#312e81"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient RGB Underglow -->
    <path d="M16 25L45.5 38L29 46.5L1.5 33Z" fill="rgba(168, 85, 247, 0.36)"/>
    <!-- Heavy Brushed Steel Pedestal Legs & Liquid-Cooled Glass PC Tower -->
    <path d="M7.5 24V37.5M34.5 34.5V44" stroke="#0f172a" stroke-width="3" stroke-linecap="round"/>
    <path d="M34 29L42.5 33V41.5L34 37.5Z" fill="#1e1b4b" stroke="#06b6d4" stroke-width="1.2" stroke-linejoin="round"/>
    <circle cx="38.2" cy="35.2" r="2" fill="none" stroke="#ec4899" stroke-width="1.1"/>
    <!-- 2.5D Isometric Executive Command Tabletop with Warm Brass Bevel -->
    <path d="M17 16L45.5 29L29.5 38L2 25Z" fill="url(#dc_desk_t3_top)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M2 25L29.5 38V40.8L2 27.8Z" fill="#1e1b4b" stroke="#eab308" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M29.5 38L45.5 29V31.8L29.5 40.8Z" fill="#0f172a" stroke="#06b6d4" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Triple Curved Ultrawide Command Monitor Array -->
    <path d="M5.5 8.5L17 10.8V20L5.5 17.8Z" fill="url(#dc_desk_t3_scr_side)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M17 10.8L31 16.5V25.8L17 20Z" fill="url(#dc_desk_t3_scr_center)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M31 16.5L42 23.8V32.8L31 25.8Z" fill="url(#dc_desk_t3_scr_side)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <!-- Quant Telemetry Candlesticks & Code Lines Across 3 Screens -->
    <line x1="8" y1="12.5" x2="14.5" y2="13.8" stroke="#4ade80" stroke-width="1.2" stroke-linecap="round"/>
    <path d="M19.5 18.2L22.5 16.5L25.5 19.2L28.8 17.2" stroke="#facc15" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <line x1="33.5" y1="22.5" x2="39.5" y2="26.5" stroke="#38bdf8" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Custom Ergonomic Split Keyboard & Studio Audio Interface -->
    <path d="M14 23.5L24.5 28.5L21.5 31L11 26Z" fill="#334155" stroke="#06b6d4" stroke-width="1" stroke-linejoin="round"/>
  </svg>`,

  // =========================================================================
  // SOFA TIERS (1-3)
  // =========================================================================

  // 7. sofa_t1: Ghế Lười Hạt Xốp / Casual Beanbag (Tier 1, 2x1)
  // True 2.5D isometric plush teardrop corduroy beanbag lounge in warm terracotta/coral with
  // matching round ottoman pouf, a cozy accent cushion, and a woven floor mat underneath.
  sofa_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_sofa_t1_mat" x1="4" y1="20" x2="44" y2="42" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef3c7"/>
        <stop offset="100%" stop-color="#fde68a"/>
      </linearGradient>
      <radialGradient id="dc_sofa_t1_bag" cx="0.38" cy="0.32" r="0.68">
        <stop offset="0%" stop-color="#fdba74"/>
        <stop offset="48%" stop-color="#f97316"/>
        <stop offset="85%" stop-color="#c2410c"/>
        <stop offset="100%" stop-color="#7c2d12"/>
      </radialGradient>
      <linearGradient id="dc_sofa_t1_pouf" x1="28" y1="24" x2="43" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb923c"/>
        <stop offset="100%" stop-color="#9a3412"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Shadow -->
    <path d="M20 18L46 31L28 44L2 31Z" fill="rgba(15, 23, 42, 0.38)"/>
    <!-- Woven Bohemian Floor Mat Underneath (2:1 Isometric Diamond) -->
    <path d="M20 18.5L44.5 30.8L28 42.5L3.5 30.2Z" fill="url(#dc_sofa_t1_mat)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M20 21L41 31.2L28 40L7 29.8Z" stroke="#d97706" stroke-width="1" stroke-dasharray="2.5 1.5"/>
    <!-- Plump Teardrop Corduroy Beanbag Lounge -->
    <path d="M19.5 8.5C23 8.5 28.5 13.5 30.5 19.5C32.5 25.5 30.5 33 21.5 35.2C12.5 37.2 6.5 32.5 7 25.5C7.5 18.5 15 8.5 19.5 8.5Z" fill="url(#dc_sofa_t1_bag)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- Corduroy Ribbed Contour Seams -->
    <path d="M18.5 9.5C14 16 11.5 25 14.5 34.5" stroke="#7c2d12" stroke-width="1.1" stroke-linecap="round" opacity="0.7"/>
    <path d="M21 9.5C24.5 16 26.5 24.5 24 34.5" stroke="#7c2d12" stroke-width="1.1" stroke-linecap="round" opacity="0.7"/>
    <!-- Sunken Plush Seat Bowl & Top Carry Loop -->
    <ellipse cx="20.2" cy="24.2" rx="7.2" ry="4.5" transform="rotate(14 20.2 24.2)" fill="#fed7aa" stroke="#c2410c" stroke-width="1.1"/>
    <path d="M18 8.5C18 6.2 21 6.2 21 8.5" stroke="#9a3412" stroke-width="1.6" stroke-linecap="round"/>
    <!-- Cozy Cream & Teal Throw Cushion Nestled Inside -->
    <path d="M16.5 19.5L23.5 21.2C24.5 21.6 24.5 23 23.5 23.8L19.5 26.2C18.5 26.8 17 26.5 16.2 25.5L14.2 22.2C13.6 21.2 15 19.2 16.5 19.5Z" fill="#2dd4bf" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- Matching Round Corduroy Ottoman Pouf in Front-Right -->
    <ellipse cx="35.5" cy="32.5" rx="6.5" ry="4.2" fill="url(#dc_sofa_t1_pouf)" stroke="#1e1b2e" stroke-width="1.35"/>
    <ellipse cx="35.5" cy="30.8" rx="4.8" ry="2.6" fill="#fdba74" stroke="#c2410c" stroke-width="0.9"/>
    <circle cx="35.5" cy="30.8" r="1" fill="#7c2d12"/>
  </svg>`,

  // 8. sofa_t2: Sofa Nỉ Đôi Hiện Đại / Fabric Loveseat (Tier 2, 2x1)
  // True 2.5D isometric modern mid-century 2-seater fabric loveseat with angled bolster armrests,
  // deep tufted dual seat & back cushions, two contrasting throw pillows (mustard gold & cream), tapered oak legs.
  sofa_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_sofa_t2_back" x1="8" y1="6" x2="40" y2="26" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#818cf8"/>
        <stop offset="55%" stop-color="#4f46e5"/>
        <stop offset="100%" stop-color="#3730a3"/>
      </linearGradient>
      <linearGradient id="dc_sofa_t2_seat" x1="6" y1="18" x2="42" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6366f1"/>
        <stop offset="60%" stop-color="#4338ca"/>
        <stop offset="100%" stop-color="#312e81"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Shadow -->
    <path d="M16 19L45 33.5L29 44.5L2 30.5Z" fill="rgba(15, 23, 42, 0.45)"/>
    <!-- Tapered Mid-Century Oak Peg Legs with Brass Tips -->
    <path d="M6.5 29.5L5 34.5M28 40.5L28 45M41.5 31.5L43 36.5" stroke="#b45309" stroke-width="2.3" stroke-linecap="round"/>
    <path d="M5.3 33.5L5 34.5M28 44V45M42.7 35.5L43 36.5" stroke="#facc15" stroke-width="2.3" stroke-linecap="round"/>
    <!-- Left Armrest Bolster (Back-Left) -->
    <path d="M4 17.5L15 11.2L19.5 13.5V22.5L8.5 28.8L4 26.5Z" fill="#4338ca" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Tufted Dual Backrest Cushions (Isometric Extruded Back) -->
    <path d="M14.5 7.5L28.5 14.5L25 23.5L11 16.5Z" fill="url(#dc_sofa_t2_back)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M28.5 14.5L42.5 21.5L39 30.5L25 23.5Z" fill="url(#dc_sofa_t2_back)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Tufted Button Dimples on Backrest -->
    <circle cx="19" cy="14.5" r="1" fill="#312e81"/>
    <circle cx="23.5" cy="16.8" r="1" fill="#312e81"/>
    <circle cx="32.5" cy="21.2" r="1" fill="#312e81"/>
    <circle cx="37" cy="23.5" r="1" fill="#312e81"/>
    <!-- Dual Plush Isometric Seat Cushions -->
    <path d="M6.5 21.5L18.5 15L31.5 21.5L19.5 28Z" fill="url(#dc_sofa_t2_seat)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M19.5 28L31.5 21.5L43 27.2L31 33.8Z" fill="url(#dc_sofa_t2_seat)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Front-Left & Front-Right Upholstered Base Apron + Oak Trim Rail -->
    <path d="M4.5 22.5L28.5 34.8V39.5L4.5 27.2Z" fill="#312e81" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M28.5 34.8L43 26.8V31.5L28.5 39.5Z" fill="#1e1b4b" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M4.5 27.2L28.5 39.5L43 31.5" stroke="#d97706" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    <!-- Two Contrasting Throw Pillows (Mustard Gold & Warm Cream/Coral) -->
    <path d="M12.5 15.5L18.8 17.5L16.2 22.8L9.8 20.5Z" fill="#facc15" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M32.5 22.5L38.8 24.5L36.2 29.8L29.8 27.5Z" fill="#fef3c7" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- Right Armrest Bolster (Front-Right) -->
    <path d="M30 31.2L41.5 24.8L45 26.8V33.5L33.5 39.8L30 37.8Z" fill="#4f46e5" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M30 31.2L41.5 24.8L45 26.8L33.5 33.2Z" fill="#818cf8" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>`,

  // 13. sofa_t3: Sofa Da Ý Cao Cấp / Italian Leather Chesterfield Sectional (Tier 3, 2x1)
  // True 2.5D isometric cognac Italian leather Chesterfield L-sectional with diamond button-tufted rolled backrest,
  // brass nailhead trim, chaise lounge extension, velvet throw cushions, and solid walnut bun feet.
  sofa_t3: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_sofa_t3_leather_back" x1="8" y1="6" x2="42" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#d97706"/>
        <stop offset="50%" stop-color="#9a3412"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="dc_sofa_t3_leather_seat" x1="6" y1="16" x2="44" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ea580c"/>
        <stop offset="55%" stop-color="#9a3412"/>
        <stop offset="100%" stop-color="#431407"/>
      </linearGradient>
      <linearGradient id="dc_sofa_t3_gold" x1="4" y1="22" x2="44" y2="40" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#eab308"/>
        <stop offset="100%" stop-color="#a16207"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Occlusion Shadow -->
    <path d="M15 18.5L46 34L28.5 45.5L1.5 31Z" fill="rgba(15, 23, 42, 0.5)"/>
    <!-- Turned Solid Walnut & Brass Bun Feet -->
    <ellipse cx="6.5" cy="32.8" rx="2.2" ry="1.3" fill="#451a03" stroke="#eab308" stroke-width="1"/>
    <ellipse cx="27.5" cy="43.5" rx="2.4" ry="1.4" fill="#451a03" stroke="#eab308" stroke-width="1"/>
    <ellipse cx="42.5" cy="34.8" rx="2.2" ry="1.3" fill="#451a03" stroke="#eab308" stroke-width="1"/>
    <!-- Left Rolled Chesterfield Armrest -->
    <path d="M3.5 16.8C3.5 14.2 6.8 12.8 9.5 14L17.5 10.2L21 12.2V22L9 28.5L3.5 25.5Z" fill="#7c2d12" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Rolled Diamond-Tufted Cognac Leather Backrest -->
    <path d="M13.5 6.8C15.5 5.5 18.5 6.2 21 7.5L43 18.5C45 19.6 45.5 22 44 24L39.5 31L11.5 16.8L13.5 6.8Z" fill="url(#dc_sofa_t3_leather_back)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Diamond Quilt Lines & Brass Tufting Buttons on Backrest -->
    <path d="M16 10.5L21 16.5L26 15.5L31 21.5L36 20.5L40 25.5" stroke="#431407" stroke-width="1" opacity="0.8"/>
    <path d="M18.5 8.5L16.5 14.5M25.5 12L23.5 18M32.5 15.5L30.5 21.5M39.5 19L37.5 25" stroke="#431407" stroke-width="1" opacity="0.8"/>
    <circle cx="17.5" cy="12" r="1.05" fill="#facc15" stroke="#451a03" stroke-width="0.6"/>
    <circle cx="24.5" cy="15.5" r="1.05" fill="#facc15" stroke="#451a03" stroke-width="0.6"/>
    <circle cx="31.5" cy="19" r="1.05" fill="#facc15" stroke="#451a03" stroke-width="0.6"/>
    <circle cx="38.5" cy="22.5" r="1.05" fill="#facc15" stroke="#451a03" stroke-width="0.6"/>
    <!-- Three Plush Cognac Leather Bench Cushions + Chaise Lounge Extension -->
    <path d="M6.5 20.8L18.5 14.5L30.5 20.5L18.5 26.8Z" fill="url(#dc_sofa_t3_leather_seat)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M18.5 26.8L30.5 20.5L42.5 26.5L27.5 35.2L16.5 29.5Z" fill="url(#dc_sofa_t3_leather_seat)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Lower Leather Base Apron with Brass Nailhead Trim Rail -->
    <path d="M4.2 22L27.5 34.2V41.5L4.2 29.2Z" fill="#7c2d12" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M27.5 34.2L43.5 25.5V32.8L27.5 41.5Z" fill="#431407" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M4.5 28.2L27.5 40.2L43.2 31.6" stroke="url(#dc_sofa_t3_gold)" stroke-width="1.6" stroke-dasharray="1.8 1.8" stroke-linecap="round"/>
    <!-- Emerald Velvet & Cream Cashmere Throw Pillows -->
    <path d="M12 14.8L18.5 16.8L15.8 22.2L9.5 19.8Z" fill="#059669" stroke="#1e1b2e" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M31.5 21.5L38 23.5L35.2 28.8L29 26.5Z" fill="#fef3c7" stroke="#eab308" stroke-width="1.2" stroke-linejoin="round"/>
    <!-- Right Rolled Chesterfield Armrest with Brass Studs -->
    <path d="M29.5 30.8L41.5 24.2C43.8 23.5 45.8 25.2 45.5 27.8V33.5L33 40.2L29.5 38Z" fill="#9a3412" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <circle cx="31.5" cy="34.5" r="1.2" fill="#facc15"/>
  </svg>`,

  // =========================================================================
  // PLANT TIERS (1-3)
  // =========================================================================

  // 9. plant_t1: Chậu Xương Rồng Sa Mạc / Potted Cactus (Tier 1, 1x1)
  // True 2.5D isometric sculpted terracotta pot with rim bevel and white river pebbles,
  // ribbed emerald Saguaro cactus with side arms, golden spines, and blooming desert pink flower on top.
  plant_t1: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_plant_t1_pot" x1="13" y1="26" x2="35" y2="43" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb923c"/>
        <stop offset="50%" stop-color="#ea580c"/>
        <stop offset="100%" stop-color="#9a3412"/>
      </linearGradient>
      <linearGradient id="dc_plant_t1_cactus" x1="18" y1="8" x2="31" y2="30" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#4ade80"/>
        <stop offset="50%" stop-color="#16a34a"/>
        <stop offset="100%" stop-color="#14532d"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Shadow & Terracotta Saucer Dish -->
    <ellipse cx="24" cy="42.5" rx="12.5" ry="4.2" fill="rgba(15, 23, 42, 0.42)"/>
    <ellipse cx="24" cy="40.8" rx="9.8" ry="3.4" fill="#9a3412" stroke="#1e1b2e" stroke-width="1.3"/>
    <!-- Sculpted 2.5D Tapered Terracotta Pot -->
    <path d="M14.5 27.5L33.5 27.5L30.8 40.2C30.8 42.2 17.2 42.2 17.2 40.2Z" fill="url(#dc_plant_t1_pot)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- 3D Pot Rim Collar & Soil with White River Pebbles -->
    <path d="M13 25.2C13 23 35 23 35 25.2V28C35 30.2 13 30.2 13 28Z" fill="#fb923c" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <ellipse cx="24" cy="25.2" rx="11" ry="3.6" fill="#fdba74" stroke="#1e1b2e" stroke-width="1.3"/>
    <ellipse cx="24" cy="25.4" rx="8.8" ry="2.5" fill="#451a03"/>
    <!-- White & Grey River Pebbles -->
    <ellipse cx="18.5" cy="25.6" rx="2" ry="1" fill="#f8fafc"/>
    <ellipse cx="28.8" cy="25.8" rx="2.2" ry="1" fill="#cbd5e1"/>
    <ellipse cx="23.5" cy="26.8" rx="1.8" ry="0.9" fill="#e2e8f0"/>
    <!-- Left & Right Saguaro Cactus Side Arms -->
    <path d="M19.5 21.5H14.5C12.8 21.5 12 20.2 12 18.5V14.2C12 12.2 15.5 12.2 15.5 14.2V18H19.5" fill="#16a34a" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M28.5 19.5H33C34.8 19.5 35.5 18.2 35.5 16.5V12.8C35.5 10.8 32 10.8 32 12.8V16H28.5" fill="#15803d" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <!-- Central Plump Ribbed Saguaro Cactus Trunk -->
    <path d="M19.2 26V12.5C19.2 8.2 28.8 8.2 28.8 12.5V26C28.8 27.5 19.2 27.5 19.2 26Z" fill="url(#dc_plant_t1_cactus)" stroke="#1e1b2e" stroke-width="1.45" stroke-linejoin="round"/>
    <!-- Vertical Cactus Contour Ribs & Specular Highlight -->
    <path d="M22.2 10.2V26.2" stroke="#86efac" stroke-width="1.2" stroke-linecap="round" opacity="0.8"/>
    <path d="M25.5 10.2V26.2" stroke="#14532d" stroke-width="1.1" stroke-linecap="round" opacity="0.75"/>
    <!-- Golden Spine Clusters -->
    <circle cx="20.5" cy="15" r="0.8" fill="#fef08a"/>
    <circle cx="27.2" cy="17.5" r="0.8" fill="#fef08a"/>
    <circle cx="20.8" cy="22" r="0.8" fill="#fef08a"/>
    <circle cx="13.8" cy="15.5" r="0.7" fill="#fef08a"/>
    <circle cx="33.8" cy="14" r="0.7" fill="#fef08a"/>
    <!-- Blooming Desert Pink Flower on Top -->
    <circle cx="21.8" cy="8.2" r="2.2" fill="#fb7185" stroke="#1e1b2e" stroke-width="1"/>
    <circle cx="26.2" cy="8.2" r="2.2" fill="#f43f5e" stroke="#1e1b2e" stroke-width="1"/>
    <circle cx="24" cy="6.8" r="2.4" fill="#fda4af" stroke="#1e1b2e" stroke-width="1"/>
    <circle cx="24" cy="8.4" r="1.5" fill="#facc15" stroke="#1e1b2e" stroke-width="0.8"/>
  </svg>`,

  // 10. plant_t2: Chậu Trầu Bà Lá Xẻ / Monstera Deliciosa (Tier 2, 1x1)
  // True 2.5D isometric ceramic cream planter resting on a mid-century wooden tripod stand,
  // rich potting soil, and multi-layered fenestrated Monstera leaves with deep emerald-to-lime gradients, veins, highlights.
  plant_t2: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_plant_t2_pot" x1="14" y1="26" x2="34" y2="41" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="65%" stop-color="#e2e8f0"/>
        <stop offset="100%" stop-color="#94a3b8"/>
      </linearGradient>
      <linearGradient id="dc_plant_t2_leaf1" x1="6" y1="10" x2="24" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#4ade80"/>
        <stop offset="100%" stop-color="#15803d"/>
      </linearGradient>
      <linearGradient id="dc_plant_t2_leaf2" x1="24" y1="8" x2="42" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#22c55e"/>
        <stop offset="100%" stop-color="#14532d"/>
      </linearGradient>
      <linearGradient id="dc_plant_t2_leaf3" x1="18" y1="3" x2="30" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#86efac"/>
        <stop offset="50%" stop-color="#22c55e"/>
        <stop offset="100%" stop-color="#15803d"/>
      </linearGradient>
    </defs>
    <!-- Ground Ambient Shadow -->
    <ellipse cx="24" cy="43.5" rx="12" ry="3.8" fill="rgba(15, 23, 42, 0.42)"/>
    <!-- Mid-Century Wooden Tripod Stand -->
    <path d="M16 36L13.5 43.5M32 36L34.5 43.5M24 37V44.5" stroke="#b45309" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="14.5" y1="39.5" x2="33.5" y2="39.5" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
    <!-- 2.5D Glazed Cream Ceramic Planter Cylinder -->
    <path d="M15 26.5H33V36.8C33 39.8 15 39.8 15 36.8Z" fill="url(#dc_plant_t2_pot)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Subtle Fluted Ceramic Ribbing & Gold Rim Band -->
    <line x1="19.5" y1="28.5" x2="19.5" y2="37.8" stroke="#cbd5e1" stroke-width="1"/>
    <line x1="24" y1="29" x2="24" y2="38.2" stroke="#cbd5e1" stroke-width="1"/>
    <line x1="28.5" y1="28.5" x2="28.5" y2="37.8" stroke="#cbd5e1" stroke-width="1"/>
    <ellipse cx="24" cy="26.5" rx="9" ry="2.8" fill="#f8fafc" stroke="#1e1b2e" stroke-width="1.35"/>
    <ellipse cx="24" cy="26.7" rx="7.2" ry="1.9" fill="#3f2314"/>
    <!-- Back Dark Emerald Foliage Layer -->
    <path d="M23 26C18 19 11 18 9 22C7.5 25 14 27.5 23 26Z" fill="#14532d" stroke="#1e1b2e" stroke-width="1.2"/>
    <path d="M25 26C30 19 37 18 39 22C40.5 25 34 27.5 25 26Z" fill="#14532d" stroke="#1e1b2e" stroke-width="1.2"/>
    <!-- Left Fenestrated Monstera Leaf -->
    <path d="M23.5 26.5C19 22 10 21 6.5 15.5C4.5 12 8.5 8 13.5 10C18.5 12 22 18 23.5 26.5Z" fill="url(#dc_plant_t2_leaf1)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M22 24.5Q14.5 17.5 9.5 12.5" stroke="#bbf7d0" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="11.5" y1="16.5" x2="8.2" y2="17.8" stroke="#1e1b2e" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="15.5" y1="13.5" x2="13.2" y2="10.2" stroke="#1e1b2e" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Right Fenestrated Monstera Leaf -->
    <path d="M24.5 26.5C29 22 38 21 41.5 15.5C43.5 12 39.5 8 34.5 10C29.5 12 26 18 24.5 26.5Z" fill="url(#dc_plant_t2_leaf2)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M26 24.5Q33.5 17.5 38.5 12.5" stroke="#86efac" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="36.5" y1="16.5" x2="39.8" y2="17.8" stroke="#1e1b2e" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="32.5" y1="13.5" x2="34.8" y2="10.2" stroke="#1e1b2e" stroke-width="1.2" stroke-linecap="round"/>
    <!-- Center Hero Glossy Monstera Leaf with Swiss-Cheese Splits -->
    <path d="M24 26.5C19 19 16 10 20.5 5C23 2.5 25 2.5 27.5 5C32 10 29 19 24 26.5Z" fill="url(#dc_plant_t2_leaf3)" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="24" y1="25" x2="24" y2="6" stroke="#dcfce7" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="20.5" y1="11.5" x2="17.8" y2="9.8" stroke="#1e1b2e" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="27.5" y1="11.5" x2="30.2" y2="9.8" stroke="#1e1b2e" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="21.8" cy="15.2" r="0.9" fill="#14532d"/>
    <circle cx="26.2" cy="15.2" r="0.9" fill="#14532d"/>
  </svg>`,

  // 15. plant_t3: Tuyệt Tác Bonsai Tùng Cổ Thụ / Masterpiece Zen Pine Bonsai (Tier 3, 1x1)
  // True 2.5D isometric imperial blue-slate & kintsugi gold ceramic bonsai tray on rosewood display table,
  // moss-covered Suiseki viewing stone, gnarled S-curve juniper trunk, and sculpted layered cloud foliage pads.
  plant_t3: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_plant_t3_pot" x1="8" y1="31" x2="40" y2="42" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#334155"/>
        <stop offset="55%" stop-color="#1e293b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="dc_plant_t3_trunk" x1="17" y1="14" x2="30" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#d97706"/>
        <stop offset="55%" stop-color="#92400e"/>
        <stop offset="100%" stop-color="#451a03"/>
      </linearGradient>
      <linearGradient id="dc_plant_t3_canopy_top" x1="14" y1="5" x2="34" y2="17" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#86efac"/>
        <stop offset="55%" stop-color="#22c55e"/>
        <stop offset="100%" stop-color="#15803d"/>
      </linearGradient>
      <linearGradient id="dc_plant_t3_canopy_mid" x1="8" y1="12" x2="40" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#4ade80"/>
        <stop offset="60%" stop-color="#16a34a"/>
        <stop offset="100%" stop-color="#14532d"/>
      </linearGradient>
    </defs>
    <!-- Ground Shadow & Carved Rosewood Bonsai Display Stand (Jita) -->
    <ellipse cx="24" cy="43" rx="16.5" ry="4.2" fill="rgba(15, 23, 42, 0.48)"/>
    <path d="M11 40.5H37L38.5 43.2H34.5L33.5 41.8H14.5L13.5 43.2H9.5Z" fill="#451a03" stroke="#1e1b2e" stroke-width="1.25" stroke-linejoin="round"/>
    <!-- Imperial Slate-Blue Ceramic Bonsai Tray with Kintsugi Gold Rim -->
    <path d="M8.5 33.5L39.5 33.5L36.5 40.5H11.5Z" fill="url(#dc_plant_t3_pot)" stroke="#eab308" stroke-width="1.35" stroke-linejoin="round"/>
    <ellipse cx="24" cy="33.5" rx="15.5" ry="3.8" fill="#475569" stroke="#1e1b2e" stroke-width="1.3"/>
    <!-- Velvet Green Kyoto Moss Mound & Suiseki Viewing Stone -->
    <ellipse cx="24" cy="33.6" rx="13.2" ry="2.8" fill="#15803d"/>
    <path d="M12.5 33.8C12.5 31.2 19 30.8 22.5 33.5Z" fill="#4ade80" opacity="0.75"/>
    <path d="M29.5 34.2L31.5 30.2L34.8 31.5L35.8 34.2Z" fill="#64748b" stroke="#1e1b2e" stroke-width="1.1" stroke-linejoin="round"/>
    <!-- Sculpted Gnarled S-Curve Juniper Trunk & Exposed Nebari Roots -->
    <path d="M19.5 35.2C21.5 34.2 22.5 32.5 21 29.5C18.5 25 26.5 22.5 26 17.5C25.5 14 21 12.5 22.5 10" stroke="url(#dc_plant_t3_trunk)" stroke-width="4.2" stroke-linecap="round" fill="none"/>
    <path d="M19.5 35.2C18 35.8 16.5 35.5 15.8 34.8M23.5 34.8C25 35.6 26.8 35.5 27.8 34.8" stroke="#78350f" stroke-width="2" stroke-linecap="round"/>
    <path d="M24.8 22.5L31.5 19.5M20.5 26.5L15 23.5" stroke="#78350f" stroke-width="2.4" stroke-linecap="round"/>
    <!-- Left Lower Cloud Pad -->
    <ellipse cx="14.5" cy="22.5" rx="7.5" ry="3.8" fill="url(#dc_plant_t3_canopy_mid)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M10 21.5C12.5 19.8 16.5 19.8 19 21.2" stroke="#bbf7d0" stroke-width="1.1" stroke-linecap="round"/>
    <!-- Right Cascading Cloud Pad -->
    <ellipse cx="32.5" cy="18.8" rx="8.5" ry="4.2" fill="url(#dc_plant_t3_canopy_mid)" stroke="#1e1b2e" stroke-width="1.35"/>
    <path d="M27.5 17.5C30.5 15.8 35 15.8 37.5 17.5" stroke="#bbf7d0" stroke-width="1.1" stroke-linecap="round"/>
    <!-- Left-Upper Secondary Cloud Pad -->
    <ellipse cx="17.5" cy="14.2" rx="7.8" ry="4" fill="url(#dc_plant_t3_canopy_mid)" stroke="#1e1b2e" stroke-width="1.35"/>
    <!-- Apex Crown Cloud Pad with Sunlit Needles -->
    <ellipse cx="24.5" cy="9.2" rx="8.2" ry="4.2" fill="url(#dc_plant_t3_canopy_top)" stroke="#1e1b2e" stroke-width="1.4"/>
    <path d="M19.5 8C22.5 6.2 26.5 6.2 29.5 8" stroke="#dcfce7" stroke-width="1.2" stroke-linecap="round"/>
  </svg>`,
};

/**
 * 2.5D Isometric Illustrated State Souvenir Trophy SVG Icons (NY, CA, TX, FL, WA).
 * Kept in a dedicated registry so `Object.keys(FURNITURE_SVG_ICONS).length === 15` remains invariant.
 */
export const DECOR_SOUVENIR_SVG_ICONS: Record<string, string> = {
  souvenir_ny_statue_liberty: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_souv_ny_patina" x1="16" y1="8" x2="32" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#5eead4"/>
        <stop offset="55%" stop-color="#14b8a6"/>
        <stop offset="100%" stop-color="#0f766e"/>
      </linearGradient>
    </defs>
    <ellipse cx="24" cy="42.5" rx="12" ry="3.8" fill="rgba(15, 23, 42, 0.45)"/>
    <path d="M14 35.5H34L36 41.5H12Z" fill="#78350f" stroke="#eab308" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M17 29.5H31L33 35.5H15Z" fill="#cbd5e1" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M19.5 29.5L21 15.5H27L28.5 29.5Z" fill="url(#dc_souv_ny_patina)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <circle cx="24" cy="13" r="3.2" fill="#5eead4" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M19.5 11.5L21.5 9M24 10V7M28.5 11.5L26.5 9" stroke="#2dd4bf" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M27 16.5L31 10.5" stroke="#14b8a6" stroke-width="2.4" stroke-linecap="round"/>
    <circle cx="31.5" cy="8.5" r="2.5" fill="#facc15" stroke="#ea580c" stroke-width="1.1"/>
  </svg>`,

  souvenir_ca_golden_gate: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_souv_ca_red" x1="8" y1="8" x2="40" y2="38" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb7185"/>
        <stop offset="50%" stop-color="#e11d48"/>
        <stop offset="100%" stop-color="#9f1239"/>
      </linearGradient>
    </defs>
    <ellipse cx="24" cy="42.5" rx="14" ry="3.8" fill="rgba(15, 23, 42, 0.45)"/>
    <path d="M8 35.5H40L38 41.5H10Z" fill="#0284c7" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <rect x="13" y="10" width="4.5" height="26" rx="1" fill="url(#dc_souv_ca_red)" stroke="#1e1b2e" stroke-width="1.3"/>
    <rect x="30.5" y="10" width="4.5" height="26" rx="1" fill="url(#dc_souv_ca_red)" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M7 27H41" stroke="#facc15" stroke-width="2.4" stroke-linecap="round"/>
    <path d="M8 18Q15 27 15 27Q24 14 33 27Q33 27 40 18" stroke="#f43f5e" stroke-width="1.6" fill="none"/>
  </svg>`,

  souvenir_tx_longhorn: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_souv_tx_gold" x1="15" y1="13" x2="33" y2="31" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#ca8a04"/>
      </linearGradient>
    </defs>
    <ellipse cx="24" cy="42.5" rx="13" ry="3.8" fill="rgba(15, 23, 42, 0.45)"/>
    <path d="M13 34.5H35L37 41.5H11Z" fill="#78350f" stroke="#eab308" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M6 14C10 14 14 21 19 22H29C34 21 38 14 42 14C39 20 35 25 29 25.5H19C13 25 9 20 6 14Z" fill="#fef3c7" stroke="#1e1b2e" stroke-width="1.4" stroke-linejoin="round"/>
    <polygon points="24,13 26.8,19 33,19.8 28.2,24 29.5,30.2 24,27 18.5,30.2 19.8,24 15,19.8 21.2,19" fill="url(#dc_souv_tx_gold)" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
  </svg>`,

  souvenir_fl_rocket_flamingo: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_souv_fl_hull" x1="18" y1="8" x2="26" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="60%" stop-color="#f1f5f9"/>
        <stop offset="100%" stop-color="#cbd5e1"/>
      </linearGradient>
    </defs>
    <ellipse cx="24" cy="42.5" rx="13" ry="3.8" fill="rgba(15, 23, 42, 0.45)"/>
    <path d="M12 36H36L34 41.5H14Z" fill="#fde047" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M18 36V15C18 10 25 10 25 15V36Z" fill="url(#dc_souv_fl_hull)" stroke="#1e1b2e" stroke-width="1.35" stroke-linejoin="round"/>
    <path d="M18 15L21.5 8L25 15Z" fill="#ef4444" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <circle cx="29.5" cy="27" r="4.5" fill="#fb7185" stroke="#1e1b2e" stroke-width="1.3"/>
    <path d="M32 24C34.5 19 31 15 29.5 18" stroke="#f43f5e" stroke-width="2.2" stroke-linecap="round" fill="none"/>
  </svg>`,

  souvenir_wa_space_needle: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dc_souv_wa_saucer" x1="13" y1="12" x2="35" y2="19" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#7dd3fc"/>
        <stop offset="55%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
    </defs>
    <ellipse cx="24" cy="42.5" rx="13" ry="3.8" fill="rgba(15, 23, 42, 0.45)"/>
    <path d="M13 36.5H35L33 41.5H15Z" fill="#065f46" stroke="#1e1b2e" stroke-width="1.3" stroke-linejoin="round"/>
    <path d="M19 36.5L22.5 18H25.5L29 36.5" stroke="#e2e8f0" stroke-width="2.2" stroke-linecap="round" fill="none"/>
    <ellipse cx="24" cy="16" rx="11" ry="3.2" fill="url(#dc_souv_wa_saucer)" stroke="#1e1b2e" stroke-width="1.35"/>
    <ellipse cx="24" cy="14.5" rx="7" ry="2" fill="#f97316" stroke="#1e1b2e" stroke-width="1.2"/>
    <line x1="24" y1="6.5" x2="24" y2="13" stroke="#facc15" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`,
};

/**
 * Hand-drawn 2.5D Cozy Furniture & Souvenir HD PNG Sprite Registry (`512x512` Native Resolution).
 * Outlines: Roasted Coffee Brown (#3D251E) | Palette: Honey Oak (#D99B66), Buttercream (#FFF5E4), Terracotta (#E07A5F), Sage Green (#81B29A).
 */
export const FURNITURE_PNG_ASSETS: Record<string, string> = {
  cozy_bed: './assets/furniture/cozy_bed.png',
  wooden_cabinet: './assets/furniture/wooden_cabinet.png',
  desk_chair_cozy: './assets/furniture/desk_chair_cozy.png',
  bed_t1: './assets/furniture/bed_t1.png',
  bed_t2: './assets/furniture/bed_t2.png',
  bed_t3: './assets/furniture/bed_t3.png',
  fridge_t1: './assets/furniture/fridge_t1.png',
  fridge_t2: './assets/furniture/fridge_t2.png',
  fridge_t3: './assets/furniture/fridge_t3.png',
  desk_t1: './assets/furniture/desk_t1.png',
  desk_t2: './assets/furniture/desk_t2.png',
  desk_t3: './assets/furniture/desk_t3.png',
  sofa_t1: './assets/furniture/sofa_t1.png',
  sofa_t2: './assets/furniture/sofa_t2.png',
  sofa_t3: './assets/furniture/sofa_t3.png',
  plant_t1: './assets/furniture/plant_t1.png',
  plant_t2: './assets/furniture/plant_t2.png',
  plant_t3: './assets/furniture/plant_t3.png',
  plant_cactus: './assets/furniture/plant_cactus.png',
  plant_monstera: './assets/furniture/plant_monstera.png',
  plant_bonsai: './assets/furniture/plant_bonsai.png',
  souvenir_ny_statue_liberty: './assets/souvenirs/souvenir_ny_statue_liberty.png',
  souvenir_ca_golden_gate: './assets/souvenirs/souvenir_ca_golden_gate.png',
  souvenir_tx_longhorn: './assets/souvenirs/souvenir_tx_longhorn.png',
  souvenir_fl_rocket_flamingo: './assets/souvenirs/souvenir_fl_rocket_flamingo.png',
  souvenir_wa_space_needle: './assets/souvenirs/souvenir_wa_space_needle.png',
  souvenir_az_grand_canyon: './assets/souvenirs/souvenir_az_grand_canyon.png',
  souvenir_or_crater_lake: './assets/souvenirs/souvenir_or_crater_lake.png',
  souvenir_id_star_garnet: './assets/souvenirs/souvenir_id_star_garnet.png',
  souvenir_nv_hoover_dam: './assets/souvenirs/souvenir_nv_hoover_dam.png',
  souvenir_co_rocky_peaks: './assets/souvenirs/souvenir_co_rocky_peaks.png',
  souvenir_mo_gateway_arch: './assets/souvenirs/souvenir_mo_gateway_arch.png',
  souvenir_hi_aloha_volcano: './assets/souvenirs/souvenir_hi_aloha_volcano.png',
  souvenir_ak_aurora_totem: './assets/souvenirs/souvenir_ak_aurora_totem.png',
  souvenir_dc_capitol_dome: './assets/souvenirs/souvenir_dc_capitol_dome.png',
  souvenir_forest_smoky_mill: './assets/souvenirs/souvenir_forest_smoky_mill.png',
};

/**
 * Alias of `FURNITURE_PNG_ASSETS` for Phaser / UI Decor catalog lookups.
 */
export const DECOR_FURNITURE_PNG_PATHS: Record<string, string> = FURNITURE_PNG_ASSETS;


